import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { DateTimeFormatOptions } from 'luxon'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { publisher, releasedEvent, twitter } from '@/constants/seo'
import { Seo } from '@/layout/Seo'
import { ArticleJsonLd } from '@/layout/Seo/JsonLd'
import { useCanonicalUrl } from '@/layout/Seo/useCanonicalUrl'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getPressRelease, PressRelease } from '@/services/PressService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { noPreRenderedStaticPaths } from '@/utils/nextUtils/staticPages'
import { loadTranslations } from '@/utils/translate/translate-server'
import { PressReleaseView } from '@/views/PressReleaseView'

interface PressReleaseProps {
  pressRelease: PressRelease
}

const PressReleasePage: NextPageWithLayout<PressReleaseProps> = ({ pressRelease }) => {
  const formatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  } as DateTimeFormatOptions

  const publishedDate = new Date(pressRelease.publishDate ?? pressRelease.sys.firstPublishedAt)
  const modifiedDate = new Date(pressRelease.sys.publishedAt)

  const formattedPublishDate = publishedDate.toLocaleDateString('en-US', formatOptions)
  const formattedModifiedDate = modifiedDate.toLocaleDateString('en-US', formatOptions)

  const url = useCanonicalUrl(`${paths.press.release}/${pressRelease.slug}`)
  const description = pressRelease.excerpt ?? `Angel Press Release | ${formattedModifiedDate ?? formattedPublishDate}`

  const title = `${pressRelease.title ?? 'Press Release'} | Angel`
  const openGraph = {
    title,
    description,
    url,
    type: 'article',
    article: {
      publishedTime: pressRelease.sys.firstPublishedAt,
      modifiedTime: pressRelease.sys.publishedAt,
      section: 'Media',
      authors: [twitter],
    },
    images: pressRelease.openGraphImage
      ? [
          {
            alt: title,
            height: 900,
            url: getCloudinaryImageUrl({
              height: 900,
              path: `${pressRelease.openGraphImage}`,
              width: 1600,
            }),
            width: 1600,
          },
        ]
      : [],
  }
  return (
    <>
      <ArticleJsonLd
        articleBody={documentToPlainTextString(pressRelease.body?.json)}
        dateModified={pressRelease.sys.publishedAt}
        datePublished={pressRelease.publishDate ?? pressRelease.sys.firstPublishedAt}
        description={description}
        headline={title}
        images={[pressRelease.openGraphImage]}
        publication={releasedEvent}
        publisher={publisher}
        url={url}
      />
      <Seo canonical={url} openGraph={openGraph} title={title} description={description} />
      <PressReleaseView pressRelease={pressRelease} />
    </>
  )
}

PressReleasePage.getLayout = (page) => {
  return <DefaultLayout isDarkMode={false}>{page}</DefaultLayout>
}

export const getStaticProps = getStaticPropsErrorHandler<PressReleaseProps>(
  { filePath: 'pages/[locale]/press/release/[slug].tsx' },
  async ({ locale, next404, params, preview }) => {
    const slug = getItemOrFirstEntry(params?.slug)
    if (!slug) return next404()

    const [pressRelease, translations] = await Promise.all([
      getPressRelease(slug, preview, locale),
      loadTranslations(locale, ['common', 'account']),
    ])

    if (!pressRelease) {
      return next404(Durations.FIVE_MINUTES_IN_SECONDS)
    }

    return {
      props: {
        pressRelease,
        ...translations,
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

export const getStaticPaths = noPreRenderedStaticPaths

export default PressReleasePage
